import { BlockDraftAsyncDomain, BlockPrintOptions } from '@strength/domain';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { useMutation, useQueryOne } from '../../../../loco-sync';
import { useToast } from '../../../hooks/useToast';

type PDFGenerationHook = {
  generateWorkoutPDF: (options: WorkoutPDFGenerationOptions) => Promise<void>;
  pdfGenerating: boolean;
};

interface WorkoutPDFGenerationOptions {
  profileIds: string[];
  workoutIds: string[];
  printOptions: BlockPrintOptions;
}

interface useGeneratePDFProps {
  blockId: string;
  onComplete: (fileName: string) => void | Promise<void>;
}

export const useGenerateWorkoutPDF = ({
  blockId,
  onComplete,
}: useGeneratePDFProps): PDFGenerationHook => {
  const mutation = useMutation();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [waitingOnPrintTaskId, setWaitingOnPrintTaskId] = useState<
    string | null
  >(null);
  const { data: printTask } = useQueryOne('BlockPrintTask', {
    id: waitingOnPrintTaskId ?? '',
    blockId,
  });

  useEffect(() => {
    // useQuery to return for PrintTasks that are complete and have url
    // useEffect on this result, and both mark the PrintTask as complete / delete it and open the url in a new tab
    // Maybe also use some local state to keep track of whether this tab hit the button
    if (printTask) {
      if (printTask.status === 'completed' && printTask.pdfUrl) {
        onComplete(printTask.pdfUrl);
        setWaitingOnPrintTaskId(null);
        mutation.mutate({
          module: BlockDraftAsyncDomain.Module,
          type: 'DeletePrintTask',
          input: {
            printTaskId: printTask.id,
            blockId,
          },
        });
      } else if (printTask.status === 'failed') {
        setWaitingOnPrintTaskId(null);
        toast({
          title: t('somethingWentWrong'),
          severity: 'error',
        });
      }
    }
  }, [blockId, mutation, onComplete, printTask, t, toast]);

  const generateWorkoutPDF = async ({
    profileIds,
    printOptions,
    workoutIds,
  }: WorkoutPDFGenerationOptions) => {
    const printTaskId = v4();

    mutation.mutate(
      {
        module: BlockDraftAsyncDomain.Module,
        type: 'Print',
        input: {
          printTaskId,
          blockId,
          profileIds,
          printOptions,
          workoutIds: workoutIds,
        },
      },
      {
        onError: () => {
          toast({
            title: t('somethingWentWrong'),
            severity: 'error',
          });
        },
      },
    );

    setWaitingOnPrintTaskId(printTaskId);
  };

  return { generateWorkoutPDF, pdfGenerating: !!waitingOnPrintTaskId };
};
